import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  API_PREFIX_V2,
  GlobalWebsocketService
} from '@approot/global-websocket';
import { GenericLeg } from '@approot/models';
import {
  AlertsService,
  LegService,
  ProductsConfigService
} from '@approot/services';
import {
  exponentialDelay,
  shortUuid,
  sleep,
  SubscriptionSink
} from '@approot/utilities';
import { Memoize } from 'lodash-decorators';
import { LegComponent } from '../../leg/leg.component';

interface WebSocketPayload {
  solverTime: number;
  solvedValue: number;
  body?: string;
}

@Component({
  selector: 'app-multi-leg-solver',
  templateUrl: './multi-leg-solver.component.html',
  styleUrls: ['./multi-leg-solver.component.scss']
})
export class MultiLegSolverComponent implements OnInit, OnDestroy {
  @Input({ required: true }) public legComponents: LegComponent[];
  @Output() solverOpenChanged = new EventEmitter<boolean>();
  public solverForm: FormGroup;
  public currentlySolving = false;
  public legsNumbered: number[] = [];
  public legToApplyTo: number;
  public inputFields: string[] = [];
  private subSink = new SubscriptionSink();
  private legService = inject(LegService);
  private webSocketService = inject(GlobalWebsocketService);
  private productsConfigService = inject(ProductsConfigService);
  private alertService = inject(AlertsService);
  private webSocketUUID = 'solver_' + shortUuid();

  public ngOnInit(): void {
    this.webSocketListener();
    this.solverForm = new FormGroup({
      targetField: new FormControl(),
      inputLegNumber: new FormControl(),
      inputField: new FormControl(),
      solvedValue: new FormControl()
    });
    this.legsNumbered = Array.from(
      { length: this.selectedLegComponents.length },
      (_, i) => i + 1
    );

    this.inputFields = [
      ...new Set(
        this.selectedLegComponents.flatMap(
          legComponent => legComponent.leg.solver.inputFields
        )
      )
    ].filter(field => field && field !== 'Spot');
  }

  public ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  public changeInLegToApplyTo(value: number): void {
    this.legToApplyTo = value;
  }

  private webSocketListener(): void {
    const resubscribe = (iteration: number) => {
      this.currentlySolving = false;

      if (iteration > 5) {
        this.alertService.addAlert({
          message:
            'There was a problem setting up the multi-leg solver WebSocket',
          type: 'error'
        });
        return;
      }

      this.subSink.sink = this.webSocketService
        .getListener({
          action: `${API_PREFIX_V2}pricing`,
          uuid: this.webSocketUUID,
          take: Infinity
        })
        .subscribe({
          next: resp => {
            this.currentlySolving = false;
            if (resp.error) {
              this.alertService.addAlert({
                message: resp.error.message,
                type: 'error'
              });
            } else {
              this.handleSolvedValue(resp.payload as WebSocketPayload);
            }
          },
          complete: async () => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          },
          error: async _ => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          }
        });
    };
    resubscribe(0);
  }

  private handleSolvedValue(payload: WebSocketPayload): void {
    if (payload.solvedValue != null) {
      const inputLegNumber = this.solverForm.get('inputLegNumber').value;
      const legComponentToApply =
        this.selectedLegComponents[inputLegNumber - 1];
      const dp = this.productsConfigService.decimalPlacesForCross(
        legComponentToApply.legHelperService.valueToUseAsCross()
      );
      this.solverForm
        .get('solvedValue')
        .setValue(payload.solvedValue.toFixed(dp));
    } else if (typeof payload.body === 'string') {
      this.alertService.addAlert({
        message: payload.body,
        type: 'error'
      });
    }
  }

  public onSolverSubmit(): void {
    const payload = {
      products: this.products(),
      task: {
        taskType: 'multiLegSolver',
        taskParameters: {
          targetField: this.solverForm.get('targetField').value,
          inputField: this.solverForm.get('inputField').value,
          inputLegNumber: this.solverForm.get('inputLegNumber').value
        }
      }
    };
    this.solverForm.get('solvedValue').setValue(null);
    this.currentlySolving = true;
    this.webSocketService.sendMessage({
      action: `${API_PREFIX_V2}pricing`,
      payload,
      uuid: this.webSocketUUID
    });
  }

  public applySolvedValue(): void {
    const legComponentToApply =
      this.selectedLegComponents[this.legToApplyTo - 1];
    legComponentToApply.onApplySolver({
      type: this.solverForm.get('inputField').value.toLowerCase(),
      value: this.solverForm.get('solvedValue').value
    });
  }

  public modalChanged(event: boolean): void {
    this.solverOpenChanged.emit(event);
  }

  @Memoize()
  private get selectedLegComponents(): LegComponent[] {
    return this.legComponents.filter(legComponent => legComponent.leg.checked);
  }

  @Memoize()
  private products(): GenericLeg[] {
    return this.legService.createProductsObject(
      this.selectedLegComponents.map(legComponent => {
        return {
          product: legComponent.form.value,
          attributes: legComponent.leg.attributes
        };
      })
    );
  }
}
